import React, { useState, useEffect } from 'react';
import { FiPlus, FiUsers, FiCheck, FiClock, FiFlag, FiAlertCircle, FiLoader } from 'react-icons/fi';
import adminServices from '../../services/AdminServices';
import { useNavigate } from 'react-router-dom';

const formatDate = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleString('en-KE', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Africa/Nairobi',
    });
};

const getStatusBadge = (status) => {
    const styles = {
        Pending: 'badge-warning',
        Matched: 'badge-info',
        Assigned: 'badge-success',
        Completed: 'badge-success',
        Cancelled: 'badge-error',
        Rejected: 'badge-error',
    };
    return styles[status] || 'badge-ghost';
};

const NurseRequestTimeline = ({ activeRequest }) => {
    const navigate = useNavigate()
    const [assignments, setAssignments] = useState([]);
    const [loadingAssignments, setLoadingAssignments] = useState(false);

    useEffect(() => {
        if (activeRequest?.assignment_count > 1) {
            setLoadingAssignments(true);
            adminServices
                .getAssignedNurses(`?request=${activeRequest.id}`)
                .then((res) => {
                    const sorted = (res.data.results || []).sort(
                        (a, b) => new Date(a.accepted_at) - new Date(b.accepted_at)
                    );
                    setAssignments(sorted);
                })
                .catch((err) => {
                    console.error('Failed to fetch assignments:', err);
                    setAssignments([]);
                })
                .finally(() => setLoadingAssignments(false));
        } else {
            setAssignments(activeRequest?.get_assigned_nurse ? [activeRequest.get_assigned_nurse] : []);
        }
    }, [activeRequest]);

    if (!activeRequest) {
        return (
            <div className="card bg-base-100 shadow-xl my-4">
                <div className="card-body">
                    <div className="flex items-center gap-3 text-base-content/60">
                        <FiAlertCircle className="w-5 h-5" />
                        <span>No request data available</span>
                    </div>
                </div>
            </div>
        );
    }

    const {
        id: requestId,
        creation_time,
        matched_at,
        completed_at,
        status,
        urgency_level,
        assignment_count,
    } = activeRequest;

    const selectedAssignment = assignments.find((a) => a.is_confirmed) || assignments[assignments.length - 1];

    const timelineItems = [
        {
            id: 'request_created',
            type: 'request',
            time: creation_time,
            icon: <FiPlus className="w-4 h-4" />,
            title: 'Request Created',
            description: `Urgency: ${urgency_level}`,
            meta: `ID: #${requestId}`,
            alwaysShow: true,
            color: 'bg-sky-500',
        },
        {
            id: 'request_matched',
            type: 'request',
            time: matched_at,
            icon: <FiUsers className="w-4 h-4" />,
            title: 'First Nurse Applied',
            description: 'Request entered matching pool',
            condition: matched_at,
            color: 'bg-teal-600',
        },
        ...assignments.map((assignment, index) => ({
            id: `assignment_${assignment.id}`,
            type: 'assignment',
            time: assignment.accepted_at,
            icon: assignment.is_confirmed ? <FiCheck className="w-4 h-4" /> : <FiUsers className="w-4 h-4" />,
            title: assignment.is_confirmed ? 'Nurse Selected' : 'Nurse Accepted',
            description: assignment.nurse?.user?.full_name
                ? `${assignment.is_confirmed ? 'Selected: ' : 'Accepted by: '}${assignment.nurse.user.full_name}`
                : 'Nurse confirmed availability',
            nurseId: assignment.nurse?.id,
            isConfirmed: assignment.is_confirmed,
            color: assignment.is_confirmed ? 'bg-sky-500' : 'bg-slate-500',
            highlight: assignment.is_confirmed,
        })),
        {
            id: 'request_completed',
            type: 'request',
            time: completed_at,
            icon: <FiFlag className="w-4 h-4" />,
            title: 'Request Completed',
            description: 'Service delivered and closed',
            condition: completed_at || status === 'Completed',
            color: 'bg-green-500',
        },
    ];

    const visibleItems = timelineItems.filter(
        (item) => item.alwaysShow || item.type === 'assignment' || (item.condition && item.time)
    );

    if (assignment_count > 1 && loadingAssignments) {
        return (
            <div className="card bg-base-100 shadow-xl my-4">
                <div className="card-body">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-neutral">Request Timeline</h3>
                        <div className={`badge ${getStatusBadge(status)}`}>{status}</div>
                    </div>
                    <div className="flex items-center justify-center py-8 gap-3 text-base-content/60">
                        <FiLoader className="w-5 h-5 animate-spin text-sky-500" />
                        <span>Loading nurse applications...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (visibleItems.length === 0) {
        return (
            <div className="card bg-base-100 shadow-xl my-4">
                <div className="card-body">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-neutral">Request Timeline</h3>
                        <div className={`badge ${getStatusBadge(status)}`}>{status}</div>
                    </div>
                    <div className="flex items-center gap-3 py-4 text-base-content/60">
                        <FiClock className="w-5 h-5 animate-pulse text-sky-500" />
                        <span>Waiting for nurses to accept...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="">
            <div className="">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-base-300">
                    <h3 className="text-lg font-semibold text-neutral">Request Timeline</h3>
                    <div className="flex items-center gap-2">
                        <div className={`badge ${getStatusBadge(status)}`}>{status}</div>
                        {assignment_count > 1 && (
                            <div className="badge badge-ghost badge-sm">{assignment_count} applicants</div>
                        )}
                    </div>
                </div>

                <ul className="timeline timeline-vertical">
                    {visibleItems.map((item, index) => {
                        const isLast = index === visibleItems.length - 1;
                        const hasTime = !!item.time;
                        const isAssignment = item.type === 'assignment';

                        return (
                            <li key={item.id}>
                                {index > 0 && <hr className={hasTime ? 'bg-primary' : 'bg-base-300'} />}

                                <div className="timeline-start">
                                    <time className="text-xs font-medium text-base-content/70 whitespace-nowrap">
                                        {formatDate(item.time) || 'Pending'}
                                    </time>
                                </div>

                                <div className="timeline-middle">
                                    <div
                                        className={`
                      rounded-full flex items-center justify-center w-8 h-8
                      ${item.color} border-2 border-white
                      ${item.highlight ? 'ring-4 ring-primary/30 shadow-lg' : ''}
                      text-white transition-all duration-200
                    `}
                                    >
                                        {item.icon}
                                    </div>
                                </div>

                                <div className="timeline-end">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-sm text-neutral">
                                            {item.title}
                                        </span>
                                        {isAssignment && (
                                            <span className="badge badge-ghost badge-xs">Assignment</span>
                                        )}
                                        {item.isConfirmed && (
                                            <span className="badge badge-primary badge-xs">Selected</span>
                                        )}
                                    </div>
                                    {item.description && (
                                        <p className="text-xs text-base-content/70 mt-1">
                                            {item.description}
                                        </p>
                                    )}
                                    {item.meta && (
                                        <p className="text-xs text-base-content/50 mt-1 font-mono">
                                            {item.meta}
                                        </p>
                                    )}
                                    {item.nurseId && (
                                        <button
                                            className="text-xs text-primary hover:underline mt-2 font-medium"
                                            onClick={() => navigate(`/nurse/${item.nurseId}`)}
                                        >
                                            View nurse profile →
                                        </button>
                                    )}
                                </div>

                                {!isLast && <hr className={hasTime ? 'bg-primary' : 'bg-base-300'} />}
                            </li>
                        );
                    })}
                </ul>

                {assignment_count > 1 && (
                    <div className="alert alert-info mt-4">
                        <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                        <p className="text-sm">
                            <strong>{assignment_count} nurses applied.</strong>{' '}
                            {selectedAssignment?.is_confirmed
                                ? `${selectedAssignment.nurse?.user?.full_name || 'A nurse'} was selected for this request.`
                                : 'Admin will review and confirm the final assignment.'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NurseRequestTimeline;